import { useEffect, useState } from "react";
import { api, formatBRL, formatPerWeight, formatDate, WEIGHT_UNITS } from "@/lib/api";
import { MagnifyingGlass, ArrowsClockwise, Trash, ArrowUpRight, Plus, Clock } from "@phosphor-icons/react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useSettings } from "@/context/SettingsContext";
import UnitToggle from "@/components/UnitToggle";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

export default function Tracker() {
  const { unit } = useSettings();
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [tracked, setTracked] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshingId, setRefreshingId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/tracked");
      setTracked(r.data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!query.trim()) return;
    setSearching(true);
    try {
      const r = await api.post("/tracked", { query: query.trim() });
      toast.success(`Encontradas ${r.data.offer_count} ofertas para "${r.data.query}"`);
      setQuery("");
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao buscar");
    } finally {
      setSearching(false);
    }
  };

  const handleRefresh = async (id) => {
    setRefreshingId(id);
    try {
      const r = await api.post(`/tracked/${id}/refresh`);
      setTracked((arr) => arr.map((t) => (t.id === id ? r.data : t)));
      toast.success("Atualizado");
    } catch {
      toast.error("Erro ao atualizar");
    } finally {
      setRefreshingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/tracked/${id}`);
      setTracked((arr) => arr.filter((t) => t.id !== id));
    } catch {
      toast.error("Erro ao remover");
    }
  };

  const unitLabel = WEIGHT_UNITS.find((u) => u.value === unit)?.label || "100g";

  return (
    <div className="space-y-6" data-testid="tracker-page">
      <header>
        <div className="label-eyebrow mb-2">Rastrear Produto</div>
        <h1 className="h-display" style={{ color: "var(--text-primary)" }}>
          Coleta de Ofertas Online
        </h1>
        <p className="text-sm mt-2 max-w-2xl" style={{ color: "var(--text-secondary)" }}>
          Digite um produto e clique em <span className="gold-text">Atualizar</span>. Coletamos ofertas online e padronizamos o preço para comparação justa.
        </p>
      </header>

      <form onSubmit={handleAdd} className="surface p-4 flex flex-col md:flex-row gap-3" data-testid="track-form">
        <div className="relative flex-1">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: "var(--text-muted)" }} />
          <input
            data-testid="track-query"
            className="input-base pl-9"
            placeholder="Ex: tempero completo, sal rosa do himalaia, ração calopsita…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            required
          />
        </div>
        <button
          type="submit"
          disabled={searching}
          className="btn-gold px-6 py-2.5 rounded-sm text-xs uppercase tracking-wider flex items-center justify-center gap-2 md:w-44"
          data-testid="track-submit"
        >
          {searching ? (<><span className="spinner inline-block w-3 h-3 border border-current border-r-transparent rounded-full" /> Buscando…</>) : (<><Plus size={14} weight="bold" /> Atualizar</>)}
        </button>
      </form>

      <div className="flex justify-end">
        <UnitToggle />
      </div>

      <section className="surface" data-testid="tracked-list">
        <div className="px-6 py-4 border-b" style={{ borderColor: "var(--border-default)" }}>
          <h2 className="text-lg font-medium" style={{ color: "var(--text-primary)" }}>
            Produtos rastreados <span className="text-sm font-normal" style={{ color: "var(--text-secondary)" }}>({tracked.length})</span>
          </h2>
        </div>
        {loading ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>Carregando…</div>
        ) : tracked.length === 0 ? (
          <div className="p-8 text-sm" style={{ color: "var(--text-secondary)" }}>
            Nenhum produto rastreado. Use o campo acima para começar.
          </div>
        ) : (
          <div className="table-scroll">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left" style={{ color: "var(--text-secondary)" }}>
                  <th className="px-6 py-3 label-eyebrow">Produto</th>
                  <th className="px-6 py-3 label-eyebrow">Ofertas</th>
                  <th className="px-6 py-3 label-eyebrow">Mín. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Méd. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Máx. /{unitLabel}</th>
                  <th className="px-6 py-3 label-eyebrow">Atualizado</th>
                  <th className="px-6 py-3 label-eyebrow text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {tracked.map((t) => (
                  <tr key={t.id} className="border-t surface-hover" style={{ borderColor: "var(--border-default)" }} data-testid={`tracked-row-${t.id}`}>
                    <td className="px-6 py-3">
                      <Link to={`/rastrear/${t.id}`} className="hover:text-[var(--gold)] inline-flex items-center gap-1" style={{ color: "var(--text-primary)" }}>
                        {t.query} <ArrowUpRight size={12} />
                      </Link>
                    </td>
                    <td className="px-6 py-3" style={{ color: "var(--text-secondary)" }}>
                      {t.normalized_count ?? 0}/{t.offer_count}
                    </td>
                    <td className="px-6 py-3" style={{ color: "var(--success)" }}>{formatPerWeight(t.min_norm, unit)}</td>
                    <td className="px-6 py-3" style={{ color: "var(--gold)" }}>{formatPerWeight(t.avg_norm, unit)}</td>
                    <td className="px-6 py-3" style={{ color: "var(--text-primary)" }}>{formatPerWeight(t.max_norm, unit)}</td>
                    <td className="px-6 py-3 text-xs flex items-center gap-1" style={{ color: "var(--text-muted)" }}><Clock size={12} />{formatDate(t.last_updated)}</td>
                    <td className="px-6 py-3 text-right whitespace-nowrap">
                      <button
                        onClick={() => handleRefresh(t.id)}
                        disabled={refreshingId === t.id}
                        className="btn-ghost p-1.5 mr-2"
                        title="Atualizar"
                        data-testid={`refresh-${t.id}`}
                      >
                        <ArrowsClockwise size={14} className={refreshingId === t.id ? "spinner" : ""} />
                      </button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <button className="btn-ghost p-1.5" style={{ color: "var(--danger)" }} title="Remover" data-testid={`delete-${t.id}`}>
                            <Trash size={14} />
                          </button>
                        </AlertDialogTrigger>
                        <AlertDialogContent style={{ background: "var(--bg-surface)", border: "1px solid var(--border-default)", borderRadius: 2 }}>
                          <AlertDialogHeader>
                            <AlertDialogTitle style={{ color: "var(--text-primary)" }}>Remover rastreamento?</AlertDialogTitle>
                            <AlertDialogDescription style={{ color: "var(--text-secondary)" }}>
                              Todas as ofertas coletadas de <strong>{t.query}</strong> serão descartadas.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel className="rounded-sm" style={{ background: "transparent", border: "1px solid var(--border-default)", color: "var(--text-primary)" }}>Cancelar</AlertDialogCancel>
                            <AlertDialogAction onClick={() => handleDelete(t.id)} className="rounded-sm" style={{ background: "var(--danger)", color: "white", border: "1px solid var(--danger)" }}>Remover</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
